import React, { useEffect, useState } from 'react';
import QueryString from 'query-string';
import { Link as RouterLink } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import Page from '../Page';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import SearchIcon from '@mui/icons-material/Search';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import PostList, { postType, statsFor } from './PostList';
import Loading from '../shared/Loading';
import { useMutation, useQuery } from '@apollo/client/react';
import { CREATE_POST, DELETE_POST, GET_POSTS } from '../../graphql/post';
import ServerError from '../error/ServerError';
import { useSnackbar } from 'notistack';
import config from '../../config';
import { useAuth } from '../../hooks/use-auth';

const StatCard = ({ icon, value, label, color, backgroundColor }) => (
  <Card variant="outlined" sx={{ borderColor: '#e2e8f0', borderRadius: 1 }}>
    <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            color,
            backgroundColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: 23, lineHeight: 1 }}>
            {value}
          </Typography>
          <Typography sx={{ color: '#40536b', fontSize: 14 }}>{label}</Typography>
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const formatPostDate = (value) => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleString();
};

const NoPreviewImage = () => (
  <Box
    sx={{
      alignItems: 'center',
      bgcolor: '#f1f5f9',
      borderRadius: 1,
      color: '#64748b',
      display: 'flex',
      fontWeight: 700,
      height: 160,
      justifyContent: 'center',
      mb: 2,
      width: '100%',
    }}
  >
    No cover image
  </Box>
);

const PreviewCard = ({ post, currentUserName }) => {
  if (!post) return null;
  const stats = statsFor(post, 0);
  const image = post.coverImageUrl;

  return (
    <Card variant="outlined" sx={{ borderColor: '#e2e8f0', borderRadius: 1 }}>
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0' }}>
          <Typography sx={{ fontWeight: 800 }}>Post Preview</Typography>
        </Box>
        <Box sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Avatar src={post.user?.avatarUrl} sx={{ width: 34, height: 34 }} />
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: 13 }}>
                  {post.user?.name || currentUserName || 'Unknown author'}
                </Typography>
                <Typography sx={{ color: '#40536b', fontSize: 12 }}>
                  {formatPostDate(post.createdAt)}
                </Typography>
              </Box>
            </Stack>
            <MoreVertIcon sx={{ color: '#40536b' }} />
          </Stack>
          {image ? (
            <Box
              component="img"
              src={image}
              alt={post.title}
              sx={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 1, mb: 2 }}
            />
          ) : (
            <NoPreviewImage />
          )}
          <Typography sx={{ fontWeight: 800, fontSize: 18, mb: 1 }}>
            {post.title}
          </Typography>
          <Typography sx={{ color: '#40536b', mb: 2 }}>
            {(post.content || 'A quick update for your community.')
              .replace(/<[^>]*>/g, '')
              .slice(0, 90)}
          </Typography>
          <Stack direction="row" spacing={3} sx={{ mb: 2 }}>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <ChatBubbleOutlineIcon sx={{ color: '#40536b', fontSize: 18 }} />
              <Typography>{stats.comments}</Typography>
            </Stack>
          </Stack>
          <Button fullWidth variant="outlined">
            View Full Post
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

const InsightsCard = ({ post }) => {
  const stats = post ? statsFor(post, 0) : { comments: 0 };
  return (
    <Card variant="outlined" sx={{ borderColor: '#e2e8f0', borderRadius: 1 }}>
      <CardContent>
        <Typography sx={{ fontWeight: 800, mb: 2 }}>Post Insights</Typography>
        {[
          ['Comments', stats.comments],
        ].map(([label, value]) => (
          <Stack key={label} direction="row" justifyContent="space-between" sx={{ py: 0.85 }}>
            <Typography sx={{ color: '#40536b' }}>{label}</Typography>
            <Typography sx={{ fontWeight: 700 }}>{value}</Typography>
          </Stack>
        ))}
      </CardContent>
    </Card>
  );
};

export default function Posts() {
  const queryParams = QueryString.parse(window.location.search);
  const trainerId = queryParams.trainerId || null;
  const userId = queryParams.userId || null;
  const lastId = queryParams.lastId || null;
  const initialStatus = queryParams.status || '';
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const { enqueueSnackbar } = useSnackbar();
  const auth = useAuth();
  const navigate = useNavigate();
  const { error, loading, data, refetch } = useQuery(GET_POSTS, {
    variables: { input: { trainerId, userId, lastId } },
  });
  const [createPost] = useMutation(CREATE_POST, {
    onError: (error) => {
      enqueueSnackbar(`Error: ${error.message}`, {
        variant: 'error',
        autoHideDuration: 2000,
      });
    },
    onCompleted: (data) => {
      navigate(`/trainer/posts/${data.createPost._id}`);
    },
  });
  const [deletePost] = useMutation(DELETE_POST, {
    onError: (error) => {
      enqueueSnackbar(`Error: ${error.message}`, {
        variant: 'error',
        autoHideDuration: 2000,
      });
    },
    onCompleted: () => {
      refetch();
    },
  });

  useEffect(() => {
    refetch();
  }, [refetch, navigate]);

  useEffect(() => {
    setPage(1);
  }, [perPage, searchTerm, statusFilter, typeFilter]);

  const handleCreate = (e) => {
    e.preventDefault();
    enqueueSnackbar(`Creating...`, { autoHideDuration: 2000 });
    createPost({
      variables: {
        input: { title: 'Untitled', status: statusFilter || undefined },
      },
    });
  };

  const handleDelete = async (post) => {
    if (!window.confirm('Delete the post?')) {
      return;
    }
    enqueueSnackbar(`Deleting...`, { autoHideDuration: 2000 });
    deletePost({ variables: { id: post._id } });
  };

  if (error) return <ServerError message={error.message} />;
  if (loading) return <Loading />;
  if (!data?.posts) return <ServerError message={'Error'} />;

  const posts = data.posts;
  const currentUserName = auth.user?.displayName || auth.user?.email;
  const filteredPosts = posts.filter((post) => {
    const matchesStatus =
      statusFilter === '' ||
      post.status === statusFilter;
    const matchesType = typeFilter === 'ALL' || postType(post) === typeFilter;
    const matchesSearch = (post.title ?? '')
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    return matchesStatus && matchesType && matchesSearch;
  });
  const totalPages = Math.max(1, Math.ceil(filteredPosts.length / perPage));
  const currentPage = Math.min(page, totalPages);
  const pageStart = filteredPosts.length === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const pageEnd = Math.min(currentPage * perPage, filteredPosts.length);
  const paginatedPosts = filteredPosts.slice(pageStart - 1, pageEnd);

  const published = posts.filter((post) => post.status !== 'DRAFT').length;
  const drafts = posts.filter((post) => post.status === 'DRAFT').length;
  const totalComments = posts.reduce((sum, post, index) => sum + statsFor(post, index).comments, 0);
  const selectedPost = filteredPosts[0] || posts[0];

  return (
    <Page title={`Posts : ${config.appDisplayName}`}>
      <Container maxWidth={false}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
          <Box>
            <Typography sx={{ fontSize: 30, fontWeight: 800, color: '#071120', mb: 1 }}>
              Posts
            </Typography>
            <Typography sx={{ color: '#40536b', fontSize: 16 }}>
              Manage community posts, announcements, fitness advice, and nutrition tips.
            </Typography>
          </Box>
          <Button
            component={RouterLink}
            to="#"
            variant="contained"
            endIcon={<MoreVertIcon />}
            startIcon={<AddIcon />}
            onClick={handleCreate}
            sx={{
              backgroundColor: '#4338ca',
              px: 2.5,
              py: 1.25,
              '&:hover': { backgroundColor: '#3730a3' },
            }}
          >
            Create Post
          </Button>
        </Stack>

        <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
          <Grid item xs={12} md={4} lg={2.4}>
            <StatCard icon={<ArticleOutlinedIcon />} value={published} label="Published" color="#4338ca" backgroundColor="#edeaff" />
          </Grid>
          <Grid item xs={12} md={4} lg={2.4}>
            <StatCard icon={<RestaurantIcon />} value={drafts} label="Drafts" color="#f59e0b" backgroundColor="#fff0df" />
          </Grid>
          <Grid item xs={12} md={4} lg={2.4}>
            <StatCard icon={<ChatBubbleOutlineIcon />} value={totalComments} label="Comments" color="#6d28d9" backgroundColor="#f0e7ff" />
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid item xs={12} lg={9}>
            <Card variant="outlined" sx={{ borderColor: '#e2e8f0', borderRadius: 1 }}>
              <CardContent sx={{ pb: 0 }}>
                <Stack direction="row" justifyContent="space-between" sx={{ borderBottom: '1px solid #e2e8f0', mb: 2 }}>
                  <ToggleButtonGroup
                    exclusive
                    value={statusFilter || 'ALL'}
                    onChange={(event, value) => {
                      if (!value) return;
                      setStatusFilter(value === 'ALL' ? '' : value);
                    }}
                    size="small"
                    sx={{
                      '& .MuiToggleButton-root': {
                        border: 0,
                        borderRadius: 0,
                        px: 2.5,
                        py: 1.5,
                        fontWeight: 800,
                        textTransform: 'none',
                      },
                      '& .Mui-selected': {
                        color: '#4338ca !important',
                        backgroundColor: 'transparent !important',
                        borderBottom: '2px solid #4338ca',
                      },
                    }}
                  >
                    <ToggleButton value="ALL">All Posts</ToggleButton>
                    <ToggleButton value="PUBLIC">Published</ToggleButton>
                    <ToggleButton value="PREMIUM">Premium</ToggleButton>
                    <ToggleButton value="PRO">Pro</ToggleButton>
                    <ToggleButton value="DRAFT">Drafts</ToggleButton>
                  </ToggleButtonGroup>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Typography sx={{ color: '#40536b' }}>Sort by:</Typography>
                    <Select size="small" value="newest" sx={{ minWidth: 150 }}>
                      <MenuItem value="newest">Newest First</MenuItem>
                    </Select>
                    <IconButton sx={{ border: '1px solid #d0d9e6', borderRadius: 1 }}>
                      <ViewModuleIcon />
                    </IconButton>
                    <IconButton sx={{ border: '1px solid #4338ca', borderRadius: 1, color: '#4338ca' }}>
                      <ViewListIcon />
                    </IconButton>
                  </Stack>
                </Stack>

                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  <TextField
                    size="small"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Search posts..."
                    sx={{ width: 250 }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <SearchIcon sx={{ color: '#40536b' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Select
                    size="small"
                    value={typeFilter}
                    onChange={(event) => setTypeFilter(event.target.value)}
                    sx={{ minWidth: 150 }}
                  >
                    <MenuItem value="ALL">All Types</MenuItem>
                    <MenuItem value="Announcement">Announcement</MenuItem>
                    <MenuItem value="Fitness Tip">Fitness Tip</MenuItem>
                    <MenuItem value="Nutrition Tip">Nutrition Tip</MenuItem>
                    <MenuItem value="Challenge">Challenge</MenuItem>
                    <MenuItem value="Motivation">Motivation</MenuItem>
                    <MenuItem value="Event">Event</MenuItem>
                  </Select>
                  <Box sx={{ flex: 1 }} />
                </Stack>

                <PostList
                  posts={paginatedPosts}
                  onDelete={handleDelete}
                  currentUserName={currentUserName}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} lg={3}>
            <Stack spacing={2}>
              <PreviewCard post={selectedPost} currentUserName={currentUserName} />
              <InsightsCard post={selectedPost} />
            </Stack>
          </Grid>
        </Grid>

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 3 }}>
          <Typography sx={{ color: '#40536b' }}>
            Showing {pageStart} to {pageEnd} of {filteredPosts.length} posts
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              disabled={currentPage === 1}
              onClick={() => setPage((value) => Math.max(1, value - 1))}
              variant="outlined"
            >
              ‹
            </Button>
            {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
              <Button
                key={pageNumber}
                onClick={() => setPage(pageNumber)}
                variant={pageNumber === currentPage ? 'contained' : 'text'}
                sx={{ minWidth: 34 }}
              >
                {pageNumber}
              </Button>
            ))}
            <Button
              disabled={currentPage === totalPages}
              onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
              variant="outlined"
            >
              ›
            </Button>
          </Stack>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Typography sx={{ color: '#40536b' }}>Show</Typography>
            <Select
              size="small"
              value={perPage}
              onChange={(event) => setPerPage(Number(event.target.value))}
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
            </Select>
            <Typography sx={{ color: '#40536b' }}>per page</Typography>
          </Stack>
        </Stack>
      </Container>
    </Page>
  );
}
